import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  SearchBox,
  Dropdown,
  IDropdownOption,
  DefaultButton,
  PrimaryButton,
  Modal,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Persona,
  PersonaSize,
  IconButton,
  mergeStyles
} from '@fluentui/react';
import {
  NewsRegular,
  AddRegular,
  EditRegular,
  DeleteRegular,
  ShareRegular,
  HeartRegular,
  EyeRegular,
  CalendarRegular,
  TagRegular,
  ImageRegular,
  ChevronRightRegular,
  StarRegular
} from '@fluentui/react-icons';
import styles from './NewsSection.module.css';
import { authenticatedFetch } from '../../utils/authApi';

// Function to get category-based gradients
const getCategoryGradient = (category: string): string => {
  const gradients: { [key: string]: string } = {
    'announcements': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'updates': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'features': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'guides': 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'news': 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'releases': 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'tips': 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'security': 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'performance': 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    'maintenance': 'linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)',
    'tutorials': 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
    'documentation': 'linear-gradient(135deg, #fdbb2d 0%, #22c1c3 100%)',
    'bugfixes': 'linear-gradient(135deg, #ee9ca7 0%, #ffdde1 100%)',
    'improvements': 'linear-gradient(135deg, #c3cfe2 0%, #c3cfe2 100%)',
    'default': 'linear-gradient(135deg, #e0c3fc 0%, #9bb5ff 100%)'
  };
  
  return gradients[category.toLowerCase()] || gradients.default;
};

interface NewsArticle {
  id: string;
  title: string;
  summary: string;
  content: string;
  category: string;
  author: string;
  tags: string[];
  featured_image?: string;
  additional_images: string[];
  is_featured: boolean;
  publish_date: string;
  created_date: string;
  modified_date: string;
  view_count: number;
  likes: number;
  status: string;
}

interface NewsSectionProps {
  isAdmin?: boolean;
}

const NewsSection: React.FC<NewsSectionProps> = ({ isAdmin = false }) => {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<NewsArticle[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadNewsData();
  }, []);

  const loadNewsData = async () => {
    try {
      setLoading(true);
      
      // Load articles, featured articles, and categories in parallel
      const [articlesResponse, featuredResponse, categoriesResponse] = await Promise.all([
        authenticatedFetch('/api/news?limit=12'),
        authenticatedFetch('/api/news/featured?limit=3'),
        authenticatedFetch('/api/news/categories')
      ]);

      const articlesData = await articlesResponse.json();
      const featuredData = await featuredResponse.json();
      const categoriesData = await categoriesResponse.json();

      if (articlesData.success) {
        setArticles(articlesData.articles);
      }

      if (featuredData.success) {
        setFeaturedArticles(featuredData.articles);
      }

      if (categoriesData.success) {
        setCategories(['all', ...categoriesData.categories]);
      }

    } catch (error) {
      console.error('Failed to load news data:', error);
      setError('Failed to load news articles');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    
    if (!newSearchTerm) {
      loadNewsData();
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/news/search?q=${encodeURIComponent(newSearchTerm)}`);
      const data = await response.json();
      
      if (data.success) {
        setArticles(data.articles);
      }
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleCategoryFilter = async (category: string) => {
    setSelectedCategory(category);
    
    try {
      const url = category === 'all' 
        ? '/api/news?limit=12' 
        : `/api/news?category=${encodeURIComponent(category)}&limit=12`;
      
      const response = await authenticatedFetch(url);
      const data = await response.json();
      
      if (data.success) {
        setArticles(data.articles);
      }
    } catch (error) {
      console.error('Filter failed:', error);
    }
  };

  const openArticleModal = async (article: NewsArticle) => {
    try {
      // Fetch full article details (this will increment view count)
      const response = await authenticatedFetch(`/api/news/${article.id}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedArticle(data.article);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to load article details:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const categoryOptions: IDropdownOption[] = categories.map(cat => ({
    key: cat,
    text: cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)
  }));

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size={SpinnerSize.large} label="Loading latest news..." />
      </div>
    );
  }

  if (error) {
    return (
      <MessageBar messageBarType={MessageBarType.error}>
        {error}
      </MessageBar>
    );
  }

  return (
    <div className={styles.newsContainer}>
      {/* Header Section */}
      <div className={styles.newsHeader}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack tokens={{ childrenGap: 8 }}>
            <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
              <NewsRegular className={styles.headerIcon} />
              <Text variant="xxLarge" className={styles.headerTitle}>
                Latest News & Updates
              </Text>
            </Stack>
            <Text variant="mediumPlus" className={styles.headerSubtitle}>
              Stay updated with the latest features, releases, and how-to guides
            </Text>
          </Stack>
          
          {isAdmin && (
            <PrimaryButton
              iconProps={{ iconName: 'Add' }}
              text="Create Article"
              className={styles.createButton}
              // onClick={() => setIsEditorOpen(true)}
            />
          )}
        </Stack>
      </div>

      {/* Search and Filter Section */}
      <div className={styles.searchSection}>
        <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="end">
          <SearchBox
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(_, newValue) => handleSearch(newValue || '')}
            className={styles.searchBox}
          />
          <Dropdown
            placeholder="Select category"
            options={categoryOptions}
            selectedKey={selectedCategory}
            onChange={(_, option) => handleCategoryFilter(option?.key as string)}
            className={styles.categoryDropdown}
          />
        </Stack>
      </div>

      {/* Featured Articles Section */}
      {featuredArticles.length > 0 && (
        <div className={styles.featuredSection}>
          <Text variant="xLarge" className={styles.sectionTitle}>
            <StarRegular className={styles.sectionIcon} />
            Featured Articles
          </Text>
          
          <div className={styles.featuredGrid}>
            {featuredArticles.map((article) => (
              <div 
                key={article.id} 
                className={styles.featuredCard}
                onClick={() => openArticleModal(article)}
              >
                <div className={styles.featuredImageContainer}>
                  <div 
                    className={styles.gradientBackground}
                    style={{ background: getCategoryGradient(article.category) }}
                  >
                    <div className={styles.gradientIcon}>
                      <NewsRegular />
                    </div>
                    <div className={styles.gradientOverlay}>
                      <Text variant="mediumPlus" className={styles.gradientText}>
                        {article.category}
                      </Text>
                    </div>
                  </div>
                  <div className={styles.featuredBadge}>
                    <StarRegular />
                    <Text variant="small">Featured</Text>
                  </div>
                </div>
                
                <div className={styles.featuredContent}>
                  <div className={styles.featuredMeta}>
                    <Text variant="small" className={styles.category}>
                      {article.category}
                    </Text>
                    <Text variant="small" className={styles.date}>
                      {getTimeAgo(article.publish_date)}
                    </Text>
                  </div>
                  
                  <Text variant="large" className={styles.featuredTitle}>
                    {article.title}
                  </Text>
                  
                  <Text variant="medium" className={styles.featuredSummary}>
                    {article.summary}
                  </Text>
                  
                  <div className={styles.featuredStats}>
                    <Stack horizontal tokens={{ childrenGap: 16 }}>
                      <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                        <EyeRegular className={styles.statIcon} />
                        <Text variant="small">{article.view_count}</Text>
                      </Stack>
                      <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                        <HeartRegular className={styles.statIcon} />
                        <Text variant="small">{article.likes}</Text>
                      </Stack>
                    </Stack>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regular Articles Grid */}
      <div className={styles.articlesSection}>
        <Text variant="xLarge" className={styles.sectionTitle}>
          <NewsRegular className={styles.sectionIcon} />
          Recent Articles
        </Text>
        
        <div className={styles.articlesGrid}>
          {articles.map((article) => (
            <div 
              key={article.id} 
              className={styles.articleCard}
              onClick={() => openArticleModal(article)}
            >
              <div className={styles.articleImageContainer}>
                <div 
                  className={styles.gradientBackground}
                  style={{ background: getCategoryGradient(article.category) }}
                >
                  <div className={styles.gradientIcon}>
                    <NewsRegular />
                  </div>
                  <div className={styles.gradientOverlay}>
                    <Text variant="medium" className={styles.gradientText}>
                      {article.category}
                    </Text>
                  </div>
                </div>
              </div>
              
              <div className={styles.articleContent}>
                <div className={styles.articleMeta}>
                  <Text variant="small" className={styles.category}>
                    {article.category}
                  </Text>
                  <Text variant="small" className={styles.date}>
                    {getTimeAgo(article.publish_date)}
                  </Text>
                </div>
                
                <Text variant="mediumPlus" className={styles.articleTitle}>
                  {article.title}
                </Text>
                
                <Text variant="medium" className={styles.articleSummary}>
                  {article.summary}
                </Text>
                
                <div className={styles.articleFooter}>
                  <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <Text variant="small" className={styles.author}>
                      By {article.author}
                    </Text>
                    
                    <Stack horizontal tokens={{ childrenGap: 12 }}>
                      <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                        <EyeRegular className={styles.statIcon} />
                        <Text variant="small">{article.view_count}</Text>
                      </Stack>
                      <ChevronRightRegular className={styles.readMoreIcon} />
                    </Stack>
                  </Stack>
                </div>
                
                {article.tags.length > 0 && (
                  <div className={styles.articleTags}>
                    {article.tags.slice(0, 3).map((tag, index) => (
                      <div key={index} className={styles.tag}>
                        <TagRegular />
                        <Text variant="small">{tag}</Text>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Article Detail Modal */}
      <Modal
        isOpen={isModalOpen}
        onDismiss={() => setIsModalOpen(false)}
        isBlocking={false}
        containerClassName={styles.modalContainer}
      >
        {selectedArticle && (
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
                <div className={styles.modalTitleSection}>
                  <Text variant="xxLarge" className={styles.modalTitle}>
                    {selectedArticle.title}
                  </Text>
                  
                  <div className={styles.modalMeta}>
                    <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
                      <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                        <CalendarRegular />
                        <Text variant="small">{formatDate(selectedArticle.publish_date)}</Text>
                      </Stack>
                      <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                        <Text variant="small">By {selectedArticle.author}</Text>
                      </Stack>
                      <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
                        <EyeRegular />
                        <Text variant="small">{selectedArticle.view_count} views</Text>
                      </Stack>
                    </Stack>
                  </div>
                </div>
                
                <IconButton
                  iconProps={{ iconName: 'Cancel' }}
                  onClick={() => setIsModalOpen(false)}
                  className={styles.closeButton}
                />
              </Stack>
            </div>
            
            <div className={styles.modalImageContainer}>
              <div 
                className={styles.modalGradientBackground}
                style={{ background: getCategoryGradient(selectedArticle.category) }}
              >
                <div className={styles.modalGradientIcon}>
                  <NewsRegular />
                </div>
                <div className={styles.modalGradientOverlay}>
                  <Text variant="xLarge" className={styles.modalGradientTitle}>
                    {selectedArticle.category}
                  </Text>
                  <Text variant="medium" className={styles.modalGradientSubtitle}>
                    {selectedArticle.title}
                  </Text>
                </div>
              </div>
            </div>
            
            <div className={styles.modalBody}>
              <div 
                className={styles.articleContent}
                dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
              />
              
              {selectedArticle.tags.length > 0 && (
                <div className={styles.modalTags}>
                  <Text variant="medium" className={styles.tagsTitle}>Tags:</Text>
                  <div className={styles.tagsContainer}>
                    {selectedArticle.tags.map((tag, index) => (
                      <div key={index} className={styles.modalTag}>
                        <TagRegular />
                        <Text variant="small">{tag}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className={styles.modalFooter}>
              <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Stack horizontal tokens={{ childrenGap: 16 }}>
                  <DefaultButton
                    iconProps={{ iconName: 'Share' }}
                    text="Share"
                  />
                  <DefaultButton
                    iconProps={{ iconName: 'Heart' }}
                    text={`Like (${selectedArticle.likes})`}
                  />
                </Stack>
                
                {isAdmin && (
                  <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <IconButton
                      iconProps={{ iconName: 'Edit' }}
                      title="Edit Article"
                    />
                    <IconButton
                      iconProps={{ iconName: 'Delete' }}
                      title="Delete Article"
                    />
                  </Stack>
                )}
              </Stack>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NewsSection;
